/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    getFriendlyName,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import {
    ArrayExtensions,
    buildCacheKeyWithUrlTokens,
    CategoriesInput,
    CurrentCategoryInput,
    getCategoriesHierarchy,
    removeDomainQspFromUrl
} from '@msdyn365-commerce-modules/retail-actions';

import { ICategoryPageSummaryConfig } from './category-page-summary.props.autogenerated';
import { IDefaultPageSummaryConfig } from '@msdyn365-commerce-modules/page-summary';
import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { createCanonicalUrl } from '../../common/utilities/createCanonicalUrl';

/**
 * Category Page Summary Input
 */
export class CategoryPageSummaryInput implements IActionInput {
    public requestContext: IRequestContext;

    public config: ICategoryPageSummaryConfig | IDefaultPageSummaryConfig;

    constructor(config: ICategoryPageSummaryConfig | IDefaultPageSummaryConfig, requestContext: IRequestContext) {
        this.config = config || {};
        this.requestContext = requestContext;
    }

    public getCacheObjectType = (): string => 'CategoryPageSummary';

    public getCacheKey = (): string =>
        `${buildCacheKeyWithUrlTokens(`CategoryPageSummary-${this.requestContext.locale}`, this.requestContext)}-${this.requestContext
            .locale ?? ''}`;

    public dataCacheType = (): CacheType => 'request';
}

const createGetCurrentCategoryInput = (
    inputData: IActionContext,
    config?: ICategoryPageSummaryConfig | IDefaultPageSummaryConfig
): CurrentCategoryInput => {
    if (inputData && inputData.requestContext) {
        let includeRootCategory: boolean | undefined;
        if (config) {
            includeRootCategory = (config as ICategoryPageSummaryConfig).includeRootCategory;
        }
        return new CurrentCategoryInput(inputData.requestContext, includeRootCategory);
    }

    throw new Error('Please specify categoryId query string in request.');
};
/**
 * Get skip count.
 * @param inputData
 */
function getSkipCount(inputData: ICreateActionContext<IGeneric<IAny>, IGeneric<IAny>>): string | undefined {
    return inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query.skip;
}

const action = async (input: CategoryPageSummaryInput, context: IActionContext): Promise<IPageSummaryData> => {
    const { config } = input;
    let categoryHierarchy: CategoryHierarchy[] | null = null;
    try {
        const getCurrentCategoryInput = createGetCurrentCategoryInput(context, input.config);
        const fullCategoryHierarchy = await getCategoriesHierarchy(new CategoriesInput(context.requestContext, true, 1000), context);
        categoryHierarchy = findCategoryHierarchy(fullCategoryHierarchy, getCurrentCategoryInput.categoryId!);
    } catch {
        // Do nothing, if the action fails fallback to values defined in data
    }
    if (ArrayExtensions.hasElements(categoryHierarchy)) {
        const currentCategory = categoryHierarchy[0];

        const locale = context.requestContext.locale;
        let names = categoryHierarchy.map(c => getFriendlyName(locale, c.NameTranslations) ?? '').reverse();

        if (locale === 'fr-CA') {
            names = ['catégories', ...names];
        } else {
            names = ['categories', ...names];
        }

        let canonicalUrl = createCanonicalUrl(context, names, `${currentCategory.RecordId}.c`);
        const skipCount = getSkipCount(context);
        const Url = new URL(canonicalUrl);
        const urlParams: URLSearchParams = new URLSearchParams(Url.search);
        if (skipCount) {
            urlParams.set('skip', skipCount);
        }

        const page = context.requestContext.url.requestUrl.searchParams?.get('page');
        if (page) {
            urlParams.set('page', page);
        }

        Url.search = urlParams.toString();
        canonicalUrl = Url.toString();
        canonicalUrl = removeDomainQspFromUrl(canonicalUrl, context.requestContext);

        const title = currentCategory.ExtensionProperties?.find(ep => ep.Key === 'l10nKeyword')?.Value?.StringValue ?? currentCategory.Name;

        return {
            title: `${title} | Henry\'s`,
            description: currentCategory.ExtensionProperties?.find(ep => ep.Key === 'l10nDesc')?.Value?.StringValue,
            // keywords: currentCategory.ExtensionProperties?.find(ep => ep.Key === 'l10nKeyword')?.Value?.StringValue,
            sharingImageUrl: currentCategory.OfflineImage,
            canonicalUrl,
            faviconUrl: config && config.faviconUrl
        };

        // If the action fails fallback to values defined from data
    } else if (config) {
        return {
            title: config.title,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};

/**
 * Finds the given category in the list of categories and returns an array with the info on where it's located.
 * @param categories - Categories list to search from.
 * @param categoryId - The category id to find.
 * @returns A list of categories starting from the given category id, ending up the root parent.
 */
const findCategoryHierarchy = (categories: CategoryHierarchy[], categoryId: number): CategoryHierarchy[] | null => {
    for (const category of categories) {
        if (category.RecordId === categoryId) {
            return [category];
        }

        if (ArrayExtensions.hasElements(category.Children)) {
            const innerCategoryHierarchy = findCategoryHierarchy(category.Children, categoryId);
            if (ArrayExtensions.hasElements(innerCategoryHierarchy)) {
                innerCategoryHierarchy.push(category);
                return innerCategoryHierarchy;
            }
        }
    }

    return null;
};

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/category-page-summary',
    action: <IAction<IPageSummaryData>>action,
    input: (args: ICreateActionContext) => {
        return new CategoryPageSummaryInput(<IDefaultPageSummaryConfig>args.config, args.requestContext);
    }
});
